// components/dialog/CreateBackupDialog.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Backup } from "@mui/icons-material";
import { getResponsiveDialogProps } from "../../theme/responsive";

const CreateBackupDialog = ({ open, onClose, site, sites = [], onCreate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [formData, setFormData] = useState({
    backupType: "MANUAL",
    notes: "",
  });

  // Get the effective site - either passed in or selected
  const effectiveSite = site || sites.find((s) => s.id === selectedSiteId);

  useEffect(() => {
    if (open) {
      setFormData({
        backupType: "MANUAL",
        notes: "",
      });
      setError("");
      // If a site is passed, use it; otherwise reset selected site
      setSelectedSiteId(site?.id || "");
    }
  }, [open, site]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!effectiveSite) {
      setError("Please select a site");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onCreate(effectiveSite.id, formData);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to create backup");
    } finally {
      setLoading(false);
    }
  };

  const getBackupTypeDescription = (type) => {
    switch (type) {
      case "MANUAL":
        return "Manual backup created by user";
      case "SCHEDULED":
        return "Automated backup based on schedule";
      case "PRE_UPDATE":
        return "Automatic backup before system updates";
      case "PRE_MIGRATION":
        return "Automatic backup before environment migration";
      default:
        return "";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      {...getResponsiveDialogProps("medium")}
      PaperProps={{
        sx: { borderRadius: 2 }, // modern rounded corners
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Backup />
          <Typography variant="h6" fontWeight={700}>
            Create Backup{effectiveSite ? ` - ${effectiveSite.name}` : ""}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Creating a backup may take several minutes depending on your site
          size.
        </Alert>

        {/* Show site selector if no site is pre-selected */}
        {!site && sites.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Site *</InputLabel>
            <Select
              value={selectedSiteId}
              label="Select Site *"
              onChange={(e) => setSelectedSiteId(e.target.value)}
              required
            >
              {sites.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name} ({s.domain})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Backup Type</InputLabel>
          <Select
            value={formData.backupType}
            label="Backup Type"
            onChange={handleChange("backupType")}
          >
            <MenuItem value="MANUAL">Manual Backup</MenuItem>
            <MenuItem value="SCHEDULED">Scheduled Backup</MenuItem>
            <MenuItem value="PRE_UPDATE">Pre-Update Backup</MenuItem>
            <MenuItem value="PRE_MIGRATION">Pre-Migration Backup</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getBackupTypeDescription(formData.backupType)}
        </Typography>

        <FormControl fullWidth>
          <InputLabel>Notes (Optional)</InputLabel>
          <Select
            value={formData.notes}
            label="Notes (Optional)"
            onChange={handleChange("notes")}
          >
            <MenuItem value="">No notes</MenuItem>
            <MenuItem value="Before plugin update">
              Before plugin update
            </MenuItem>
            <MenuItem value="Before theme change">Before theme change</MenuItem>
            <MenuItem value="Regular maintenance">Regular maintenance</MenuItem>
            <MenuItem value="Before content update">
              Before content update
            </MenuItem>
          </Select>
        </FormControl>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> This will create a full backup including:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20, marginTop: 4 }}>
            <li>All WordPress files and directories</li>
            <li>Database with all content and settings</li>
            <li>Uploads and media files</li>
            <li>Themes and plugins</li>
          </ul>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !effectiveSite}
          disableElevation
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          startIcon={loading ? <CircularProgress size={16} /> : <Backup />}
        >
          {loading ? "Creating Backup..." : "Create Backup"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBackupDialog;
